/* ==========================================================================
   NasalStrip.uk — theme.js
   Vanilla JS, no dependencies. Progressive enhancement over Shopify forms.
   ========================================================================== */
(() => {
  'use strict';

  const T = window.theme || {};
  const ROUTES = Object.assign({ root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update' }, T.routes || {});
  const STR = Object.assign({ addToCart: 'Add to bag', adding: 'Adding…', soldOut: 'Sold out', added: 'Added', removed: 'removed', undo: 'Undo', error: 'Something went wrong. Please try again.' }, T.strings || {});
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); }; };
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* ------------------------------------------------------------------
     Money
  ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    const fmt = format || T.moneyFormat || '£{{amount}}';
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : Math.round(Number(cents) || 0);
    const withDelimiters = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision);
      const [whole, dec] = fixed.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (dec ? decimal + dec : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2);
      }
    });
  }
  T.formatMoney = formatMoney;

  /* ------------------------------------------------------------------
     Toasts
  ------------------------------------------------------------------ */
  function toast(message, type = 'info', timeout = 3200) {
    const region = $('[data-toast-region]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
    el.setAttribute('role', type === 'error' ? 'alert' : 'status');
    el.textContent = message;
    region.appendChild(el);
    setTimeout(() => { el.style.transition = 'opacity .3s'; el.style.opacity = '0'; setTimeout(() => el.remove(), 320); }, timeout);
  }

  /* ------------------------------------------------------------------
     Focus trap
  ------------------------------------------------------------------ */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container) {
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* ------------------------------------------------------------------
     Drawer base
  ------------------------------------------------------------------ */
  let openDrawers = 0;
  class DrawerElement extends HTMLElement {
    get panel() { return this.querySelector('.drawer__panel'); }
    // Tracked as a flag (not only the CSS class, which is added a frame later) so quick double calls can't leave the page locked
    get isOpen() { return this._isOpen === true || (this._isOpen === undefined && this.classList.contains('is-open')); }
    open(opener) {
      if (this.isOpen) return;
      this._isOpen = true;
      this.opener = opener || document.activeElement;
      clearTimeout(this._hideTimer);
      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => { if (this._isOpen) this.classList.add('is-open'); }));
      openDrawers += 1;
      document.body.classList.add('scroll-lock', 'drawer-open');
      if (this._release) this._release();
      this._release = trapFocus(this);
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this._onKey);
      clearTimeout(this._focusTimer);
      this._focusTimer = setTimeout(() => { if (this._isOpen) (this.panel || this).focus({ preventScroll: true }); }, 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.isOpen) return;
      this._isOpen = false;
      this.classList.remove('is-open');
      openDrawers = Math.max(0, openDrawers - 1);
      const others = Array.from(document.querySelectorAll('.drawer')).some((d) => d !== this && d.isOpen);
      if (!others) { openDrawers = 0; document.body.classList.remove('scroll-lock', 'drawer-open'); }
      if (this._release) { this._release(); this._release = null; }
      if (this._onKey) { document.removeEventListener('keydown', this._onKey); this._onKey = null; }
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => { if (!this._isOpen) this.hidden = true; }, 560);
      if (this.opener && typeof this.opener.focus === 'function' && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }

  /* ------------------------------------------------------------------
     Cart API + section rendering
  ------------------------------------------------------------------ */
  const Cart = {
    sectionIds() {
      const ids = new Set();
      $$('[data-cart-section]').forEach((el) => ids.add(el.dataset.cartSection));
      return Array.from(ids);
    },
    async post(url, body) {
      const sections = this.sectionIds();
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(Object.assign({}, body, sections.length ? { sections, sections_url: window.location.pathname } : {}))
      });
      let data = null;
      try { data = await res.json(); } catch (e) { /* ignore */ }
      if (!res.ok || (data && data.status && data.status >= 400)) {
        const msg = (data && (data.description || data.message)) || STR.error;
        throw new Error(msg);
      }
      return data || {};
    },
    async add(items) {
      const data = await this.post(ROUTES.cartAdd + '.js', { items });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { items } }));
      return data;
    },
    async change(key, quantity) {
      const data = await this.post(ROUTES.cartChange + '.js', { id: key, quantity });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      return data;
    },
    async update(payload) {
      const data = await this.post(ROUTES.cartUpdate + '.js', payload);
      if (data.sections) this.render(data.sections);
      return data;
    },
    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const sep = window.location.search ? '&' : '?';
      const res = await fetch(window.location.pathname + window.location.search + sep + 'sections=' + ids.join(','));
      if (res.ok) this.render(await res.json());
    },
    render(sections) {
      if (!sections) return;
      let count = null;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-inner]');
        const cartEl = doc.querySelector('[data-cart]');
        if (cartEl && count === null) count = parseInt(cartEl.dataset.itemCount, 10);
        $$(`[data-cart-section="${CSS.escape(id)}"]`).forEach((host) => {
          const target = host.querySelector('[data-cart-inner]');
          if (!target || !fresh) return;
          const scroller = target.querySelector('[data-cart-scroll]');
          const scrollTop = scroller ? scroller.scrollTop : 0;
          const oldBar = target.querySelector('.reward__bar');
          const oldWidth = oldBar ? oldBar.dataset.progress : null;
          target.innerHTML = fresh.innerHTML;
          const newScroller = target.querySelector('[data-cart-scroll]');
          if (newScroller) newScroller.scrollTop = scrollTop;
          const newBar = target.querySelector('.reward__bar');
          if (newBar && oldWidth !== null && !reducedMotion) {
            const to = newBar.dataset.progress;
            newBar.style.transition = 'none';
            newBar.style.width = oldWidth + '%';
            void newBar.offsetWidth;
            newBar.style.transition = '';
            requestAnimationFrame(() => { newBar.style.width = to + '%'; });
          }
        });
      });
      if (count !== null && !Number.isNaN(count)) this.setCount(count);
    },
    setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        // The welcome pop-up stores the count as data only: never replace its content or unhide it
        if (el.tagName === 'WELCOME-POPUP') { el.dataset.cartCount = count; return; }
        const changed = el.textContent.trim() !== String(count);
        el.textContent = count;
        el.hidden = count === 0;
        if (changed && count > 0 && !reducedMotion) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
      });
      $$('[data-cart-open]').forEach((el) => el.setAttribute('aria-label', `Open bag, ${count} items`));
    }
  };
  T.Cart = Cart;

  const cartDrawer = () => document.getElementById('CartDrawer');
  function openCart(opener) {
    const drawer = cartDrawer();
    if (drawer && T.cartType !== 'page') { drawer.open(opener); return true; }
    return false;
  }
  T.openCart = openCart;

  /* ------------------------------------------------------------------
     Cart drawer + line item controller (shared by drawer and cart page)
  ------------------------------------------------------------------ */
  class CartDrawer extends DrawerElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-cart-close]')) { e.preventDefault(); this.close(); } });
    }
  }
  define('cart-drawer', CartDrawer);

  const pendingQty = new Map();
  const sendQty = debounce(async (line, key) => {
    const qty = pendingQty.get(key);
    pendingQty.delete(key);
    line.classList.add('is-loading');
    try { await Cart.change(key, qty); }
    catch (err) { toast(err.message, 'error'); Cart.refresh(); }
  }, 380);

  function drawerNotice(message) {
    const drawer = cartDrawer();
    const box = drawer && drawer.isOpen ? drawer.querySelector('[data-cart-toast]') : null;
    if (!box) { toast(message); return; }
    box.innerHTML = '';
    const span = document.createElement('span');
    span.textContent = message;
    box.append(span);
    box.hidden = false;
    clearTimeout(box._t);
    box._t = setTimeout(() => { box.hidden = true; }, 2600);
  }

  function showUndo(line) {
    const host = line.closest('cart-drawer');
    const title = line.dataset.title || 'Item';
    const variantId = parseInt(line.dataset.variantId, 10);
    const qty = parseInt(line.dataset.qty, 10) || 1;
    const box = host ? host.querySelector('[data-cart-toast]') : null;
    if (!box) { toast(`${title} ${STR.removed}`); return; }
    box.innerHTML = '';
    const span = document.createElement('span');
    span.textContent = `${title} ${STR.removed}`;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = STR.undo;
    btn.addEventListener('click', async () => {
      box.hidden = true;
      try { await Cart.add([{ id: variantId, quantity: qty }]); } catch (err) { toast(err.message, 'error'); }
    });
    box.append(span, btn);
    box.hidden = false;
    clearTimeout(box._t);
    box._t = setTimeout(() => { box.hidden = true; }, 6000);
  }

  document.addEventListener('click', async (e) => {
    // Open cart
    const opener = e.target.closest('[data-cart-open]');
    if (opener && openCart(opener)) { e.preventDefault(); return; }

    // Quantity steppers inside cart lines
    const step = e.target.closest('.line [data-qty-step]');
    if (step) {
      const line = step.closest('.line');
      const input = line.querySelector('[data-qty-input]');
      const key = line.dataset.key;
      const current = pendingQty.has(key) ? pendingQty.get(key) : parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + parseInt(step.dataset.qtyStep, 10));
      input.value = next;
      pendingQty.set(key, next);
      if (next === 0) { line.classList.add('is-removing'); showUndo(line); }
      sendQty(line, key);
      return;
    }

    // Remove
    const remove = e.target.closest('.line [data-remove]');
    if (remove) {
      const line = remove.closest('.line');
      line.classList.add('is-removing');
      showUndo(line);
      try { await Cart.change(line.dataset.key, 0); } catch (err) { line.classList.remove('is-removing'); toast(err.message, 'error'); }
      return;
    }

    // Bundle upgrade nudge
    const nudge = e.target.closest('.line [data-set-qty]');
    if (nudge) {
      const line = nudge.closest('.line');
      line.classList.add('is-loading');
      try {
        await Cart.change(line.dataset.key, parseInt(nudge.dataset.setQty, 10));
        drawerNotice('Bundle upgraded. Nice choice.');
      } catch (err) { line.classList.remove('is-loading'); toast(err.message, 'error'); }
      return;
    }

    // Quick add (cards, hero chip, upsells, kits)
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      if (quick.getAttribute('aria-busy') === 'true') return;
      let items;
      try { items = JSON.parse(quick.dataset.quickAdd); } catch (err) { return; }
      quick.classList.add('is-loading');
      quick.setAttribute('aria-busy', 'true');
      try {
        await Cart.add(items);
        quick.classList.remove('is-loading');
        quick.classList.add('is-success');
        setTimeout(() => quick.classList.remove('is-success'), 1400);
        const drawer = cartDrawer();
        if (!(drawer && drawer.isOpen)) {
          if (!openCart(quick)) window.location.href = ROUTES.cart;
        }
      } catch (err) {
        toast(err.message, 'error');
      } finally {
        quick.classList.remove('is-loading');
        quick.removeAttribute('aria-busy');
      }
    }
  });

  // Typed quantity in cart lines
  document.addEventListener('change', (e) => {
    const input = e.target.closest('.line [data-qty-input]');
    if (input) {
      const line = input.closest('.line');
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      pendingQty.set(line.dataset.key, qty);
      sendQty(line, line.dataset.key);
      return;
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) Cart.update({ note: note.value }).catch(() => {});
  });

  // Keep the cart fresh when returning via back/forward cache
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    $$('.is-loading, .is-removing').forEach((el) => el.classList.remove('is-loading', 'is-removing'));
    $$('[aria-busy="true"]').forEach((el) => el.removeAttribute('aria-busy'));
    $$('sticky-atc').forEach((el) => { if (typeof el.setLoading === 'function') el.setLoading(false); });
    $$('welcome-popup').forEach((el) => { if (!el.classList.contains('is-open')) el.hidden = true; });
    const anyOpen = Array.from(document.querySelectorAll('.drawer')).some((d) => d.isOpen);
    if (!anyOpen) { openDrawers = 0; document.body.classList.remove('scroll-lock', 'drawer-open'); }
    Cart.refresh().catch(() => {});
  });

  /* ------------------------------------------------------------------
     Header: sticky behaviour + mobile menu
  ------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.hideOnScroll = this.classList.contains('hide-on-scroll');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 8);
        if (this.hideOnScroll && !document.body.classList.contains('drawer-open')) {
          const goingDown = y > this.lastY + 4;
          const goingUp = y < this.lastY - 4;
          if (goingDown && y > 320) this.classList.add('is-hidden');
          else if (goingUp || y < 120) this.classList.remove('is-hidden');
        }
        this.lastY = y;
        this.ticking = false;
      });
    }
  }
  define('sticky-header', StickyHeader);

  class MenuDrawer extends DrawerElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-menu-close]')) { e.preventDefault(); this.close(); } });
      this.addEventListener('drawer:open', () => $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'true')));
      this.addEventListener('drawer:close', () => $$('[data-menu-open]').forEach((b) => b.setAttribute('aria-expanded', 'false')));
    }
  }
  define('menu-drawer', MenuDrawer);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-menu-open]');
    if (!btn) return;
    const menu = document.getElementById('MenuDrawer');
    if (menu) menu.open(btn);
  });

  /* ------------------------------------------------------------------
     Dispatch countdown (UK time) + delivery estimate
  ------------------------------------------------------------------ */
  const UK_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
  const DAY_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
  const WEEKDAY_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long' });
  function ukParts() {
    const p = {};
    UK_FMT.formatToParts(new Date()).forEach(({ type, value }) => { p[type] = parseInt(value, 10); });
    return p;
  }
  const isWorkday = (d) => { const wd = d.getUTCDay(); return wd !== 0 && wd !== 6; };
  const addWorkdays = (date, n) => { const d = new Date(date); let added = 0; while (added < n) { d.setUTCDate(d.getUTCDate() + 1); if (isWorkday(d)) added += 1; } return d; };
  const nextWorkday = (date) => addWorkdays(date, 1);

  class DispatchTimer extends HTMLElement {
    connectedCallback() {
      this.line = this.querySelector('[data-dispatch-line]');
      this.eta = this.querySelector('[data-dispatch-eta]');
      this.tick();
      this.timer = setInterval(() => this.tick(), 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const cfg = T.dispatch || { cutoff: 14, min: 2, max: 4 };
      const p = ukParts();
      const today = new Date(Date.UTC(p.year, p.month - 1, p.day));
      const minutesNow = p.hour * 60 + p.minute;
      const cutoff = cfg.cutoff * 60;
      let dispatchDay = today;
      let html;
      if (isWorkday(today) && minutesNow < cutoff) {
        const left = cutoff - minutesNow;
        const h = Math.floor(left / 60);
        const m = left % 60;
        const time = h > 0 ? `${h}h ${m}m` : `${m}m`;
        html = `Order within <strong>${time}</strong> for <strong>same-day dispatch</strong>`;
      } else {
        dispatchDay = nextWorkday(today);
        const diffDays = Math.round((dispatchDay - today) / 86400000);
        const when = diffDays === 1 ? 'tomorrow' : WEEKDAY_FMT.format(dispatchDay);
        html = `Order now for dispatch <strong>${when}</strong> from the UK`;
      }
      if (this.line) this.line.innerHTML = html;
      if (this.eta) {
        const from = addWorkdays(dispatchDay, cfg.min);
        const to = addWorkdays(dispatchDay, cfg.max);
        this.eta.innerHTML = `Tracked delivery <strong>${DAY_FMT.format(from)} – ${DAY_FMT.format(to)}</strong>`;
      }
    }
  }
  define('dispatch-timer', DispatchTimer);

  /* ------------------------------------------------------------------
     Media gallery
  ------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.counter = this.querySelector('[data-gallery-counter]');
      this.index = 0;
      if (!this.track) return;
      this.track.addEventListener('scroll', () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
          if (i !== this.index) this.setActive(i);
        });
      }, { passive: true });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.galleryThumb, 10))));
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(this.index + 1);
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
      });
    }
    go(i) {
      const n = this.slides.length;
      if (!n) return;
      const idx = (i + n) % n;
      this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.setActive(idx);
    }
    goToMedia(mediaId) {
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx > -1) this.go(idx);
    }
    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, k) => { t.classList.toggle('is-active', k === i); t.toggleAttribute('aria-current', k === i); });
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
      const active = this.thumbs[i];
      if (active && active.parentElement && active.parentElement.parentElement) {
        const list = active.parentElement.parentElement;
        const r = active.getBoundingClientRect();
        const lr = list.getBoundingClientRect();
        if (r.left < lr.left || r.right > lr.right || r.top < lr.top || r.bottom > lr.bottom) active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }
  }
  define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------
     Product page: bundles, variants, pairing, add to cart, sticky ATC
  ------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.page = this.closest('product-page');
      this.form = document.getElementById(this.dataset.formId);
      this.select = this.querySelector('[data-satc-select]');
      this.button = this.querySelector('[data-satc-add]');
      this.priceEl = this.querySelector('[data-satc-price]');
      this.wasEl = this.querySelector('[data-satc-was]');
      // On phones the buy box sits below the gallery, so the bar is shown from the start; on larger screens it's already in view.
      const always = this.dataset.mode === 'always' && window.matchMedia('(max-width: 989px)').matches;
      const target = this.page && (always ? this.page.querySelector('[data-buy-box]') : null) || (this.page && this.page.querySelector('[data-atc-sentinel]'));
      if (!target || !('IntersectionObserver' in window)) return;

      // "always": show whenever the real buy box is off screen (above or below).
      // "after": show only once the shopper has scrolled past it.
      this.io = new IntersectionObserver(([entry]) => {
        const show = always ? entry.intersectionRatio < 0.4 : (!entry.isIntersecting && entry.boundingClientRect.top < 0);
        this.toggle(show);
      }, { threshold: always ? [0, 0.4] : 0 });
      this.io.observe(target);

      if (this.select) {
        this.select.addEventListener('change', () => {
          const radio = this.page.querySelector(`[data-tier][data-index="${this.select.value}"]`);
          if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
        });
      }
      if (this.button) {
        this.button.addEventListener('click', () => {
          if (!this.form) return;
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
          else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      }
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      $$('button, select', this).forEach((el) => { el.tabIndex = show ? 0 : -1; });
    }
    sync(index) { if (this.select && this.select.value !== String(index)) this.select.value = String(index); }
    setPrice(price, was) {
      if (this.priceEl) this.priceEl.textContent = price;
      if (this.wasEl) { this.wasEl.textContent = was; this.wasEl.hidden = !was; }
    }
    setAvailability(available, text) {
      if (!this.button) return;
      this.button.disabled = !available;
      const span = this.button.querySelector('span');
      if (span) span.textContent = text;
    }
    setLoading(on) { if (this.button) this.button.classList.toggle('is-loading', on); }
  }
  define('sticky-atc', StickyAtc);

  class ProductPage extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      this.data = json ? JSON.parse(json.textContent) : { variants: [] };
      this.form = document.getElementById(this.dataset.formId);
      this.variant = this.data.variants.find((v) => v.id === this.data.selected) || this.data.variants[0];
      this.variantInput = this.form ? this.form.querySelector('[data-variant-input]') : null;
      this.atc = this.form ? this.form.querySelector('[data-atc]') : null;
      this.error = this.querySelector('[data-form-error]');
      this.sticky = this.querySelector('sticky-atc');

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-tier]')) this.onTier(e.target);
        else if (e.target.matches('[data-option-index]')) this.onOption();
        else if (e.target.matches('[data-pair]')) this.updatePrices();
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      // Product-page quantity stepper (when no bundle picker)
      this.addEventListener('click', (e) => {
        const step = e.target.closest('.pdp__form [data-qty-step]');
        if (!step) return;
        const input = this.form.querySelector('[data-qty-input]');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(step.dataset.qtyStep, 10));
        this.updatePrices();
      });
      this.updatePrices();
    }

    get selectedTier() { return this.querySelector('[data-tier]:checked'); }

    quantity() {
      const tier = this.selectedTier;
      if (tier) return parseInt(tier.dataset.qty, 10);
      const input = this.form && this.form.querySelector('[data-qty-input]');
      return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
    }

    paid() {
      const tier = this.selectedTier;
      return tier ? parseInt(tier.dataset.paid, 10) : this.quantity();
    }

    onTier(input) {
      $$('.tier', this).forEach((el) => el.classList.toggle('is-selected', el.contains(input)));
      if (this.sticky && this.sticky.sync) this.sticky.sync(input.dataset.index);
      this.updatePrices();
    }

    onOption() {
      const pickers = $$('variant-picker', this);
      if (!pickers.length) return;
      const values = [];
      $$('[data-option-index]:checked', this).forEach((r) => { values[parseInt(r.dataset.optionIndex, 10) - 1] = r.value; });
      const match = this.data.variants.find((v) => v.options.every((o, i) => o === values[i]));
      $$('[data-option-value]', this).forEach((el, i) => { if (values[i]) el.textContent = values[i]; });
      if (!match) { this.setAvailability(false, 'Unavailable'); return; }
      this.variant = match;
      if (this.variantInput) this.variantInput.value = match.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url.toString());
      if (match.media_id) { const g = this.querySelector('media-gallery'); if (g) g.goToMedia(match.media_id); }
      this.setAvailability(match.available);
      this.updatePrices();
    }

    setAvailability(available, label) {
      const text = available ? STR.addToCart : (label || STR.soldOut);
      if (this.atc) {
        this.atc.disabled = !available;
        const l = this.atc.querySelector('[data-atc-label]');
        if (l) l.textContent = text;
      }
      if (this.sticky && this.sticky.setAvailability) this.sticky.setAvailability(available, text);
    }

    updatePrices() {
      const v = this.variant;
      if (!v) return;
      const nights = this.data.nights || 30;
      // Tier rows reflect the current variant price
      $$('[data-tier]', this).forEach((input) => {
        const q = parseInt(input.dataset.qty, 10);
        const paid = parseInt(input.dataset.paid, 10);
        const row = input.closest('.tier');
        const now = v.price * paid;
        let was = v.price * q;
        if (q === 1 && v.compare_at_price > v.price) was = v.compare_at_price;
        const priceEl = row.querySelector('[data-tier-price]');
        const wasEl = row.querySelector('[data-tier-was]');
        const ppnEl = row.querySelector('[data-tier-ppn]');
        if (priceEl) priceEl.textContent = formatMoney(now);
        if (wasEl) { wasEl.textContent = formatMoney(was); wasEl.hidden = !(was > now); }
        if (ppnEl) ppnEl.textContent = formatMoney(Math.round(now / q / nights));
      });

      const qty = this.quantity();
      const paid = this.selectedTier ? this.paid() : qty;
      let total = v.price * paid;
      let was = v.price * qty;
      if (qty === 1 && v.compare_at_price > v.price) was = v.compare_at_price;
      const pair = this.querySelector('[data-pair]:checked');
      if (pair) { total += parseInt(pair.dataset.price, 10) || 0; was += parseInt(pair.dataset.price, 10) || 0; }

      const priceText = formatMoney(total);
      const wasText = was > total ? formatMoney(was) : '';
      if (this.form) {
        const p = this.form.querySelector('[data-atc-price]');
        if (p) p.textContent = priceText;
        let w = this.form.querySelector('[data-atc-was]');
        if (!w && wasText && p) { w = document.createElement('s'); w.setAttribute('data-atc-was', ''); p.after(' ', w); }
        if (w) { w.textContent = wasText; w.hidden = !wasText; }
      }
      // Main price block follows the selected variant
      const mainPrice = this.querySelector('.pdp__price [data-price]');
      if (mainPrice) mainPrice.textContent = formatMoney(v.price);
      const mainCompare = this.querySelector('.pdp__price [data-compare]');
      if (mainCompare) { mainCompare.textContent = formatMoney(v.compare_at_price); mainCompare.hidden = !(v.compare_at_price > v.price); }

      if (this.sticky && this.sticky.setPrice) this.sticky.setPrice(priceText, wasText);
    }

    async onSubmit(e) {
      if (!window.fetch) return;
      e.preventDefault();
      if (this.atc && this.atc.getAttribute('aria-busy') === 'true') return;
      if (!this.variant || !this.atc || this.atc.disabled) return;
      const items = [{ id: this.variant.id, quantity: this.quantity() }];
      const pair = this.querySelector('[data-pair]:checked');
      if (pair) items.push({ id: parseInt(pair.dataset.variantId, 10), quantity: 1 });
      if (this.error) this.error.hidden = true;
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');
      if (this.sticky && this.sticky.setLoading) this.sticky.setLoading(true);
      try {
        await Cart.add(items);
        if (!openCart(this.atc)) window.location.href = ROUTES.cart;
      } catch (err) {
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
        else toast(err.message, 'error');
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
        if (this.sticky && this.sticky.setLoading) this.sticky.setLoading(false);
      }
    }
  }
  define('product-page', ProductPage);



  /* ------------------------------------------------------------------
     Slider arrows (reviews, rows)
  ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-slider-prev], [data-slider-next]');
    if (!btn) return;
    const section = btn.closest('section') || document;
    const slider = section.querySelector('[data-slider-scope]') || section.querySelector('[data-slider]');
    if (!slider) return;
    const card = slider.firstElementChild;
    const amount = card ? card.getBoundingClientRect().width + 16 : slider.clientWidth * 0.8;
    slider.scrollBy({ left: btn.hasAttribute('data-slider-next') ? amount : -amount, behavior: reducedMotion ? 'auto' : 'smooth' });
  });

  /* ------------------------------------------------------------------
     Reveal on scroll + count-up stats
  ------------------------------------------------------------------ */
  function countUp(el) {
    const raw = el.dataset.countUp || el.textContent;
    const m = raw.match(/^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/);
    if (!m || reducedMotion) return;
    const [, prefix, numStr, suffix] = m;
    const decimals = (numStr.split('.')[1] || '').length;
    const hasComma = numStr.includes(',');
    const target = parseFloat(numStr.replace(/,/g, ''));
    const start = performance.now();
    const dur = 1600;
    const fmt = (n) => {
      let s = n.toFixed(decimals);
      if (hasComma) s = Number(s).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return prefix + s + suffix;
    };
    const frame = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 4);
      el.textContent = fmt(target * eased);
      if (t < 1) requestAnimationFrame(frame); else el.textContent = raw;
    };
    requestAnimationFrame(frame);
  }

  function initReveal(root = document) {
    const els = $$('[data-reveal]', root);
    const counters = $$('[data-count-up]', root);
    if (!('IntersectionObserver' in window) || designMode || reducedMotion || !T.reveal) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    document.documentElement.classList.add('reveal-on');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
    const cio = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { countUp(entry.target); cio.unobserve(entry.target); } });
    }, { threshold: 0.6 });
    counters.forEach((el) => cio.observe(el));
  }


  /* ------------------------------------------------------------------
     Motion: word-by-word headline reveals and the benefits ring
  ------------------------------------------------------------------ */
  function splitWords(el) {
    if (el.classList.contains('is-split')) return;
    let w = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          if (parts.every((p) => !p.trim())) return;
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
            const outer = document.createElement('span'); outer.className = 'sw';
            const inner = document.createElement('span'); inner.className = 'sw__i'; inner.style.setProperty('--w', w++); inner.textContent = part;
            outer.appendChild(inner); frag.appendChild(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && !/^(BR|SVG|IMG)$/i.test(child.tagName)) walk(child);
      });
    };
    walk(el);
    el.classList.add('is-split');
  }
  function initSplit(root = document) {
    const els = $$('[data-split]', root);
    if (!els.length) return;
    if (reducedMotion || designMode || !T.reveal || !('IntersectionObserver' in window)) return;
    els.forEach(splitWords);
    const show = (el) => requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-in')));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -10% 0px' });
    els.forEach((el) => (el.hasAttribute('data-split-now') ? show(el) : io.observe(el)));
  }

  function initOrbit(root = document) {
    $$('[data-orbit]', root).forEach((stage) => {
      if (stage.dataset.orbitReady) return;
      stage.dataset.orbitReady = '1';
      const nodes = $$('[data-orbit-node]', stage);
      const items = $$('[data-orbit-item]', stage);
      if (!nodes.length) return;
      let current = -1; let timer = null; let paused = false;
      const set = (i) => {
        current = i;
        nodes.forEach((n, k) => n.classList.toggle('is-active', k === i));
        items.forEach((n, k) => n.classList.toggle('is-active', k === i));
      };
      const next = () => { if (!paused) set((current + 1) % nodes.length); };
      const start = () => { if (timer || reducedMotion) return; set(current < 0 ? 0 : current); timer = setInterval(next, 2600); };
      const stop = () => { clearInterval(timer); timer = null; };
      nodes.forEach((n, k) => {
        n.addEventListener('mouseenter', () => { paused = true; set(k); });
        n.addEventListener('focus', () => { paused = true; set(k); });
        n.addEventListener('click', () => { paused = true; set(k); });
        n.addEventListener('mouseleave', () => { paused = false; });
        n.addEventListener('blur', () => { paused = false; });
      });
      if (reducedMotion) { set(0); return; }
      if ('IntersectionObserver' in window) {
        let visible = false;
        new IntersectionObserver(([e]) => {
          visible = e.isIntersecting;
          if (visible) setTimeout(() => { if (visible) start(); }, 1500); else stop();
        }, { threshold: 0.35 }).observe(stage);
      } else start();
    });
  }

  /* ------------------------------------------------------------------
     Welcome offer pop-up (email sign-up with a first-order code)
  ------------------------------------------------------------------ */
  class WelcomePopup extends HTMLElement {
    connectedCallback() {
      this.key = 'ns-welcome-v1';
      this.days = Number(this.dataset.days) || 14;
      this.panel = this.querySelector('.wp__panel');
      $$('[data-wp-close]', this).forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      const copy = this.querySelector('[data-wp-copy]');
      if (copy) copy.addEventListener('click', () => this.copy(copy));

      const ownPost = location.hash === '#WelcomeForm';
      if (this.querySelector('[data-wp-success]')) {
        this.save({ s: 1 });
        if (ownPost) { this.fixApplyLink(); this.open(); }
        return;
      }
      if (ownPost && this.querySelector('[data-wp-error]')) { this.open(); return; }
      if (designMode) {
        document.addEventListener('shopify:section:select', (e) => { if (e.target.contains(this)) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(this)) this.close(); });
        return;
      }

      const st = this.load();
      if (st && (st.s || Date.now() - (st.t || 0) < this.days * 864e5)) return;
      if (Number(this.dataset.cartCount) > 0) return;

      this.blocked = false;
      document.addEventListener('cart:added', () => { this.blocked = true; });
      const delay = Math.max(10, Number(this.dataset.delay) || 30) * 1000;
      this.timer = setTimeout(() => this.maybeOpen(), delay);
      const armed = Date.now() + 8000;
      this.onLeave = (e) => { if (!e.relatedTarget && e.clientY <= 0 && Date.now() > armed) this.maybeOpen(); };
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) document.addEventListener('mouseout', this.onLeave);
    }
    disconnectedCallback() { clearTimeout(this.timer); if (this.onLeave) document.removeEventListener('mouseout', this.onLeave); }
    load() { try { return JSON.parse(localStorage.getItem(this.key) || 'null'); } catch (e) { return null; } }
    save(v) { try { localStorage.setItem(this.key, JSON.stringify(Object.assign({ t: Date.now() }, v))); } catch (e) {} }
    maybeOpen() {
      if (this.shown || this.blocked) return;
      if (document.body.classList.contains('drawer-open')) { clearTimeout(this.timer); this.timer = setTimeout(() => this.maybeOpen(), 15000); return; }
      const count = $('[data-cart-count]');
      if (count && !count.hidden && Number(count.textContent) > 0) return;
      this.open();
    }
    open() {
      this.shown = true;
      clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      if (!this.querySelector('[data-wp-success]')) this.save({});
      this.lastFocus = document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.classList.add('is-open');
        const input = this.querySelector('input[type="email"]');
        const fine = window.matchMedia('(pointer: fine)').matches;
        (fine && input ? input : this.panel).focus({ preventScroll: true });
      }));
    }
    close() {
      this.classList.remove('is-open');
      setTimeout(() => { this.hidden = true; }, 350);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      if (location.hash === '#WelcomeForm') history.replaceState(null, '', location.pathname + location.search.replace(/[?&]customer_posted=true/, '').replace(/^&/, '?'));
    }
    fixApplyLink() {
      const a = this.querySelector('[data-wp-apply]');
      if (!a) return;
      const params = new URLSearchParams(location.search);
      params.delete('customer_posted');
      const qs = params.toString();
      a.href = '/discount/' + encodeURIComponent(this.dataset.code) + '?redirect=' + encodeURIComponent(location.pathname + (qs ? '?' + qs : ''));
    }
    copy(btn) {
      const label = btn.querySelector('[data-wp-copy-label]');
      const done = () => { if (label) { label.textContent = 'Copied'; setTimeout(() => { label.textContent = 'Copy'; }, 2000); } };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(this.dataset.code).then(done, done);
      else done();
    }
  }
  define('welcome-popup', WelcomePopup);

  /* ------------------------------------------------------------------
     Airflow art (pillar cards): play the intro once the card is on
     screen, and pause the looping flow while it's off screen
  ------------------------------------------------------------------ */
  const initAirflow = (root = document) => {
    const arts = $$('[data-airflow]', root);
    if (!arts.length || reducedMotion || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        if (entry.isIntersecting) {
          el.classList.add('is-playing');
          el.classList.remove('is-paused');
        } else if (el.classList.contains('is-playing')) {
          el.classList.add('is-paused');
        }
      });
    }, { threshold: 0.6 });
    arts.forEach((el) => {
      if (el.dataset.airflowReady) return;
      el.dataset.airflowReady = '1';
      el.classList.add('is-armed');
      io.observe(el);
    });
  };

  /* ------------------------------------------------------------------
     Article tables: label each cell with its column heading so tables
     can stack into readable cards on phones (CSS in theme.css)
  ------------------------------------------------------------------ */
  const initTables = (root = document) => {
    root.querySelectorAll('.rte table').forEach((table) => {
      const heads = [...table.querySelectorAll('thead th')].map((th) => th.textContent.trim());
      if (!heads.length) return;
      table.querySelectorAll('tbody tr').forEach((tr) => {
        [...tr.children].forEach((cell, i) => { if (heads[i]) cell.setAttribute('data-label', heads[i]); });
      });
      table.classList.add('rte-table--stack');
    });
  };

  /* ------------------------------------------------------------------
     Boot
  ------------------------------------------------------------------ */
  const boot = () => { initReveal(); initSplit(); initOrbit(); initTables(); initAirflow(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => { initReveal(e.target); initSplit(e.target); initOrbit(e.target); initTables(e.target); initAirflow(e.target); });
  document.addEventListener('shopify:block:select', (e) => {
    const tier = e.target.querySelector && e.target.querySelector('[data-tier]');
    if (tier) tier.click();
  });
})();
